import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  FiArrowLeft,
  FiShield,
  FiCheckCircle,
  FiAlertCircle,
  FiLock,
  FiCreditCard,
  FiDollarSign,
  FiClock,
  FiHelpCircle,
} from "react-icons/fi";
import { BsWallet2, BsBank } from "react-icons/bs";
import { SeoHead } from "../utils/seo";
import {
  fetchAccountById,
  clearSelectedAccount,
} from "../store/slices/accountSlice";
import {
  purchaseAccount,
  initializeDeposit,
  initializeKorapayDeposit,
  initializeTransactpayDeposit,
  fetchEnabledPaymentMethods,
} from "../store/slices/paymentSlice";
import { openModal } from "../store/slices/uiSlice";
import { GlassCard } from "../components/ui/GlassCard";
import toast from "react-hot-toast";

export const CheckoutPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedAccount: account, isLoading } = useSelector(
    (state) => state.accounts,
  );
  const { user } = useSelector((state) => state.auth);
  const { isLoading: paymentLoading, enabledPaymentMethods } = useSelector(
    (state) => state.payments,
  );
  const [paymentMethod, setPaymentMethod] = useState("wallet");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    dispatch(fetchAccountById(id));
    dispatch(fetchEnabledPaymentMethods());
    return () => {
      dispatch(clearSelectedAccount());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (account && account.status !== "available") {
      toast.error("This account is no longer available");
      navigate("/accounts");
    }
  }, [account, navigate]);

  const handlePurchase = async () => {
    if (!agreeTerms) {
      toast.error("Please agree to the terms and conditions");
      return;
    }

    if (paymentMethod === "wallet" && user.balance < account.price) {
      toast.error("Insufficient balance. Please deposit funds.");
      return;
    }

    try {
      if (paymentMethod === "wallet") {
        const result = await dispatch(purchaseAccount(account._id));
        if (purchaseAccount.fulfilled.match(result)) {
          toast.success("Account purchased successfully!");
          navigate("/orders");
        }
      } else if (paymentMethod === "paystack") {
        await dispatch(initializeDeposit(account.price));
      } else if (paymentMethod === "korapay") {
        await dispatch(initializeKorapayDeposit(account.price));
      } else if (paymentMethod === "transactpay") {
        await dispatch(initializeTransactpayDeposit(account.price));
      }
    } catch (error) {
      toast.error("Transaction failed. Please try again.");
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const pageTitle = "Checkout | Nitro Logs";
  const pageDescription = account
    ? `Secure checkout for verified ${account.socialMediaType} accounts. Choose wallet, Paystack, or Korapay to complete your Nitro Logs purchase.`
    : "Secure checkout for Nitro Logs verified social media accounts. Complete your purchase with secure payments.";
  const pageUrl =
    typeof window !== "undefined"
      ? window.location.href
      : `https://nitrologs.com/checkout/${id}`;

  if (isLoading) {
    return (
      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-luxury-200 rounded w-1/4 mb-8"></div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="h-96 bg-luxury-200 rounded-2xl"></div>
              <div className="space-y-4">
                <div className="h-12 bg-luxury-200 rounded w-3/4"></div>
                <div className="h-6 bg-luxury-200 rounded w-1/2"></div>
                <div className="h-32 bg-luxury-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!account) {
    return null;
  }

  const platformColors = {
    Facebook: "from-blue-600 to-blue-400",
    Instagram: "from-purple-600 to-pink-500",
    Twitter: "from-sky-400 to-blue-500",
    WhatsApp: "from-green-500 to-emerald-400",
    Telegram: "from-blue-400 to-cyan-400",
    TikTok: "from-gray-800 to-gray-600",
    LinkedIn: "from-blue-700 to-blue-500",
    YouTube: "from-red-600 to-red-400",
  };

  const color =
    platformColors[account.socialMediaType] ||
    "from-primary-500 to-secondary-500";

  return (
    <>
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        keywords="checkout, Nitro Logs checkout, buy social media account, secure payment"
        pageUrl={pageUrl}
      />

      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-luxury-50 via-white to-luxury-100">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-6"
          >
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-luxury-600 hover:text-primary-600 transition-colors"
            >
              <FiArrowLeft className="w-5 h-5" />
              <span>Back to Account Details</span>
            </button>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Checkout Form */}
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="lg:col-span-2 space-y-6"
            >
              {/* Header */}
              <motion.div variants={fadeInUp}>
                <h1 className="text-3xl font-display font-bold text-luxury-900 mb-2">
                  Complete Your <span className="gradient-text">Purchase</span>
                </h1>
                <p className="text-luxury-600">
                  You're just a few steps away from owning this premium account
                </p>
              </motion.div>

              {/* Account Summary */}
              <motion.div variants={fadeInUp} className="glass rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <div
                    className={`w-16 h-16 rounded-xl bg-gradient-to-r ${color} p-4 flex items-center justify-center`}
                  >
                    <span className="text-2xl text-white font-bold">
                      {account.socialMediaType[0]}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-luxury-900 mb-1">
                      {account.title}
                    </h2>
                    <p className="text-sm text-luxury-500 mb-2">
                      {account.socialMediaType} •{" "}
                      {account.followers?.toLocaleString()} followers
                    </p>
                    <button
                      onClick={() => setShowDetails(!showDetails)}
                      className="text-primary-600 text-sm hover:text-primary-700 transition-colors"
                    >
                      {showDetails ? "Hide details" : "View details"}
                    </button>

                    {/* Expandable Details */}
                    {showDetails && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 pt-4 border-t border-luxury-200"
                      >
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-luxury-500">Account Age</p>
                            <p className="font-medium text-luxury-900">
                              {account.years} years
                            </p>
                          </div>
                          <div>
                            <p className="text-luxury-500">Engagement</p>
                            <p className="font-medium text-green-600">High</p>
                          </div>
                          <div>
                            <p className="text-luxury-500">Verification</p>
                            <p className="font-medium text-luxury-900">
                              Email Access
                            </p>
                          </div>
                          <div>
                            <p className="text-luxury-500">Support</p>
                            <p className="font-medium text-luxury-900">
                              24/7 Available
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold gradient-text">
                      ₦{account.price?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Payment Method */}
              <motion.div variants={fadeInUp} className="glass rounded-2xl p-6">
                <h2 className="text-xl font-semibold text-luxury-900 mb-4">
                  Payment Method
                </h2>
                <div className="space-y-3">
                  {/* Wallet Payment */}
                  <label
                    className={`block p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                      paymentMethod === "wallet"
                        ? "border-primary-500 bg-primary-50"
                        : "border-luxury-200 hover:border-primary-200"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="payment"
                          value="wallet"
                          checked={paymentMethod === "wallet"}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="w-5 h-5 text-primary-500"
                        />
                        <BsWallet2 className="w-6 h-6 text-primary-500" />
                        <div>
                          <p className="font-semibold text-luxury-900">
                            Wallet Balance
                          </p>
                          <p className="text-sm text-luxury-500">
                            Available: ₦{user?.balance?.toLocaleString() || 0}
                          </p>
                        </div>
                      </div>
                      {user?.balance >= account.price ? (
                        <span className="text-sm text-green-600 flex items-center">
                          <FiCheckCircle className="w-4 h-4 mr-1" />
                          Sufficient
                        </span>
                      ) : (
                        <span className="text-sm text-red-600 flex items-center">
                          <FiAlertCircle className="w-4 h-4 mr-1" />
                          Insufficient
                        </span>
                      )}
                    </div>
                  </label>

                  {/* Paystack Payment */}
                  {enabledPaymentMethods.includes("paystack") && (
                    <label
                      className={`block p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                        paymentMethod === "paystack"
                          ? "border-primary-500 bg-primary-50"
                          : "border-luxury-200 hover:border-primary-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="payment"
                            value="paystack"
                            checked={paymentMethod === "paystack"}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            className="w-5 h-5 text-primary-500"
                          />
                          <BsBank className="w-6 h-6 text-primary-500" />
                          <div>
                            <p className="font-semibold text-luxury-900">
                              Paystack
                            </p>
                            <p className="text-sm text-luxury-500">
                              Pay with card, bank transfer or USSD
                            </p>
                          </div>
                        </div>
                        <img
                          src="https://paystack.com/assets/img/logo.svg"
                          alt="Paystack"
                          className="h-6 opacity-50"
                        />
                      </div>
                    </label>
                  )}

                  {/* Korapay Payment */}
                  {enabledPaymentMethods.includes("korapay") && (
                    <label
                      className={`block p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                        paymentMethod === "korapay"
                          ? "border-primary-500 bg-primary-50"
                          : "border-luxury-200 hover:border-primary-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="payment"
                            value="korapay"
                            checked={paymentMethod === "korapay"}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            className="w-5 h-5 text-primary-500"
                          />
                          <div className="w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-xs">
                            K
                          </div>
                          <div>
                            <p className="font-semibold text-luxury-900">
                              Korapay
                            </p>
                            <p className="text-sm text-luxury-500">
                              Fast and secure payment
                            </p>
                          </div>
                        </div>
                        <span className="text-lg font-bold text-orange-600">
                          🟠
                        </span>
                      </div>
                    </label>
                  )}

                  {/* Transactpay Payment */}
                  {enabledPaymentMethods.includes("transactpay") && (
                    <label
                      className={`block p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                        paymentMethod === "transactpay"
                          ? "border-primary-500 bg-primary-50"
                          : "border-luxury-200 hover:border-primary-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="payment"
                            value="transactpay"
                            checked={paymentMethod === "transactpay"}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            className="w-5 h-5 text-primary-500"
                          />
                          <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xs">
                            T
                          </div>
                          <div>
                            <p className="font-semibold text-luxury-900">
                              Transactpay
                            </p>
                            <p className="text-sm text-luxury-500">
                              Multiple payment options available
                            </p>
                          </div>
                        </div>
                        <span className="text-lg font-bold text-blue-600">
                          💳
                        </span>
                      </div>
                    </label>
                  )}
                </div>
              </motion.div>

              {/* Terms & Conditions */}
              <motion.div variants={fadeInUp} className="space-y-4">
                <label className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                    className="mt-1 w-5 h-5 text-primary-500 rounded border-luxury-300"
                  />
                  <span className="text-sm text-luxury-600">
                    I agree to the{" "}
                    <Link
                      to="/terms"
                      className="text-primary-600 hover:text-primary-700"
                    >
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="/privacy"
                      className="text-primary-600 hover:text-primary-700"
                    >
                      Privacy Policy
                    </Link>
                    . I understand that all sales are final and accounts are
                    non-refundable.
                  </span>
                </label>

                {/* Security Note */}
                <div className="flex items-center space-x-2 p-4 bg-blue-50 rounded-xl">
                  <FiShield className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <p className="text-sm text-blue-700">
                    Your transaction is secure and encrypted. We never store
                    your payment information.
                  </p>
                </div>
              </motion.div>
            </motion.div>

            {/* Order Summary */}
            <motion.div variants={fadeInUp} className="lg:col-span-1">
              <GlassCard className="p-6 sticky top-24">
                <h2 className="text-xl font-semibold text-luxury-900 mb-4">
                  Order Summary
                </h2>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-luxury-600">Subtotal</span>
                    <span className="font-medium text-luxury-900">
                      ₦{account.price?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-luxury-600">Processing Fee</span>
                    <span className="font-medium text-green-600">Free</span>
                  </div>
                  <div className="border-t border-luxury-200 pt-3">
                    <div className="flex justify-between">
                      <span className="font-semibold text-luxury-900">
                        Total
                      </span>
                      <span className="text-2xl font-bold gradient-text">
                        ₦{account.price?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Purchase Button */}
                <button
                  onClick={handlePurchase}
                  disabled={
                    paymentLoading ||
                    !agreeTerms ||
                    (paymentMethod === "wallet" &&
                      user?.balance < account.price)
                  }
                  className="w-full luxury-button disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                >
                  {paymentLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Processing...
                    </div>
                  ) : (
                    <>
                      <FiLock className="inline mr-2" />
                      Complete Purchase
                    </>
                  )}
                </button>

                {/* Payment Icons */}
                <div className="flex justify-center space-x-3 mb-4">
                  <FiCreditCard className="w-6 h-6 text-luxury-400" />
                  <BsBank className="w-6 h-6 text-luxury-400" />
                  <FiDollarSign className="w-6 h-6 text-luxury-400" />
                </div>

                {/* Delivery Guarantee */}
                <div className="text-center">
                  <p className="text-xs text-luxury-500 flex items-center justify-center">
                    <FiClock className="w-3 h-3 mr-1" />
                    Instant delivery after payment
                  </p>
                </div>

                {/* Help Link */}
                <div className="mt-6 pt-4 border-t border-luxury-200 text-center">
                  <Link
                    to="/help"
                    className="text-sm text-luxury-500 hover:text-primary-600 transition-colors flex items-center justify-center"
                  >
                    <FiHelpCircle className="w-4 h-4 mr-1" />
                    Need help with your order?
                  </Link>
                </div>
              </GlassCard>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};
